import { distanceInWordsToNow } from 'date-fns';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { TransitionGroup } from 'react-transition-group';
import { useMutation } from '@apollo/client';
import { useAuthContext } from 'AuthContext';
import CommentButton from 'components/buttons/Comment';
import CommentsButton from 'components/buttons/Comments';
import LikeButton from 'components/buttons/Like';
// import ReportButton from 'components/buttons/Report';
import Card from 'components/Card';
import CardActions from 'components/Card/Actions';
import CardHeader from 'components/Card/Header';
import CardText from 'components/Card/Text';
import CardTitle from 'components/Card/Title';
import Rating from 'components/Rating';
import RowSpacer from 'components/RowSpacer';
import FadeTransition from 'components/transitions/Fade';
import Asset from 'containers/Asset';
import CommentForm from 'components/CommentForm';
import Headshot from 'containers/Headshot';
import tracker from //  TrackerFragments
  'lib/tracker';

import ReviewComments from './Comments';
import { convertToLocalDate } from '../../../utils/helper';
import { FEED_LIKE, CREATE_COMMENT } from '../../../schema/apolloMutations/FeedMutations';
// import ReviewFlagDialog from './FlagDialog';

const ReviewCard = ({ review, viewer, onLikeOrUnlike, onCommentCount, ...props }) => {
  const { handleIntercepted, interceptAction } = useAuthContext();
  const [comments, setComments] = useState(false);
  const [commenting, setCommenting] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [flagging, setFlagging] = useState(false);
  const [liking, setLiking] = useState(false);
  const [saving, setSaving] = useState(false);
  const [feedLike] = useMutation(FEED_LIKE);
  const [createComment] = useMutation(CREATE_COMMENT);
  const busy = useMemo(() => commenting || flagging || saving, [
    commenting,
    flagging,
    saving,
  ]);
  // const isAuthor = useMemo(
  //   () => viewer?.loggedIn
  //     && viewer.person
  //     && viewer.person.id === review.person.id,
  //   [review.person.id, viewer?.loggedIn, viewer.person],
  // );
  const trackerData = useMemo(
    () => ({
      review,
      viewer,
    }),
    [review, viewer],
  );
  const hideCommentForm = useCallback(() => {
    setCommenting(false);
    setComments(false);
  }, [setCommenting]);
  // const hideFlagDialog = useCallback(() => setFlagging(false), [setFlagging]);

  const onComment = useCallback(
    (
      data,
      {
        resetForm = () => { },
        setErrors = () => { },
        setSubmitting = () => { },
      } = {},
    ) => {
      setSaving(true);
      createComment({
        variables: {
          comment: {
            body: data.body,
            foreignId: review.reviewInfo.id,
            model: 'Review',
            parentId: null,
            userId: review.personInfo.id,
          },
        },
      }).then(res => {
        if (res) {
          resetForm();
          setCommenting(false);
          setSaving(false);
          setSubmitting(false);
          tracker.trackGoogleAnalyticsEvent(
            'Reviews',
            'Create comment',
            trackerData,
          );
          onCommentCount();
        }
      }).catch(e => {
        setErrors(e);
        setSaving(false);
        setSubmitting(false);
        tracker.trackGoogleAnalyticsException(e, trackerData);
      });
      setSaving(true);
    },
    [setCommenting, setSaving, trackerData],
  );
  const like = useCallback(() => {
    // TODO: Review like/Unlike mutation
    setLiking(true);
    // eslint-disable-next-line no-undef
    // TODO: onSuccess handling
    setLiking(false);
    tracker.trackGoogleAnalyticsEvent(
      'Reviews',
      review.liked ? 'Unlike' : 'Like',
      trackerData,
    );
    // TODO: onFailure handling
    //    TODO: message destructuring
      const { message } = { message: '' };
      tracker.trackGoogleAnalyticsException(message, trackerData);
  }, [review, trackerData]);
  const onLike = useCallback(() => {
    setLiking(true);
    feedLike({
      variables: {
        feedType: review.feedType,
        id: review.reviewInfo.id,
        personId: review.personInfo.id,
      },
    }).then(res => {
      if (res) {
        onLikeOrUnlike(review);
        setLiking(false);
      }
    });
    // if (viewer?.loggedIn) {
    //   like();
    // } else {
    //   interceptAction(`like-${review.id}`);
    // }
  }, [interceptAction, like, review.id, viewer?.loggedIn]);
  const showCommentForm = useCallback(() => {
    setCommenting(true);
    // setComments(true);
  }, [setCommenting]);
  // const showFlagDialog = useCallback(() => setFlagging(true), [setFlagging]);
  const toggleComments = useCallback(() => {
    if (review.commentCount === 0) {
      showCommentForm();
    } else {
      setComments(!comments);
    }
  }, [comments, review.commentCount, showCommentForm]);
  useEffect(() => {
    if (viewer?.loggedIn) {
      handleIntercepted(`like-${review.id}`, like);
    }
  }, [
    handleIntercepted,
    like,
    review.id,
    viewer?.loggedIn,
    comments,
    commenting,
  ]);
  return (
    <Card {...props}>
      <CardHeader
        avatar={<Headshot person={review.personInfo} />}
        title={review.personInfo.displayName}
        subheader={`${distanceInWordsToNow(
          convertToLocalDate(review.feedInfo.created),
        )} ago`}
      />
      {useMemo(
        () => review.reviewInfo.assetInfo && (
          <Asset asset={review.reviewInfo.assetInfo} />
        ),
        [review.reviewInfo],
      )}
      {useMemo(
        () => review.reviewInfo.title && (
          <CardTitle noTopPadding title={review.reviewInfo.title} />
        ),
        [review.reviewInfo.title],
      )}
      {useMemo(
        () => review.reviewInfo.body && (
          <CardText noTopPadding>{review.reviewInfo.body}</CardText>
        ),
        [review.reviewInfo.body],
      )}
      {useMemo(
        () => review.reviewInfo.rating
          && review.reviewInfo.rating > 0 && (
            <CardText noTopPadding>
              <Rating value={review.reviewInfo.rating} readOnly />
            </CardText>
          ),
        [review.reviewInfo.rating],
      )}
      <CardActions noTopPadding>
        <LikeButton
          disabled={liking}
          liked={review.feedInfo.liked}
          likes={review.feedInfo.likeCount}
          onClick={onLike}
          primary={!liking}
        />
        <CommentsButton
          disabled={busy}
          comments={review.feedInfo.commentCount}
          onClick={toggleComments}
          primary={!busy}
        />
        <CommentButton
          disabled={busy}
          onClick={showCommentForm}
          primary={!busy}
        />
        <RowSpacer />
        {/* {useMemo(
          () => !isAuthor && (
            <ReportButton
              disabled={busy}
              flagged={review.flagged}
              onClick={showFlagDialog}
              primary={!busy}
            />
          ),
          [busy, isAuthor, review.flagged, showFlagDialog],
        )} */}
      </CardActions>
      <TransitionGroup>
        {useMemo(
          () => commenting && (
            <FadeTransition key='reply-form'>
              <CommentForm
                autoFocus
                onCancel={hideCommentForm}
                onSave={onComment}
                saving={saving}
              />
            </FadeTransition>
          ),
          [commenting, hideCommentForm, onComment, saving],
        )}
      </TransitionGroup>
      {useMemo(
        () => comments
          && review.feedInfo.commentCount > 0 && (
            <ReviewComments review={review} />
          ),
        [review.feedInfo.commentCount, comments],
      )}
      {/* {useMemo(
        () => (!isAuthor ? (
          <ReviewFlagDialog
            review={review}
            onRequestClose={hideFlagDialog}
            open={flagging}
            viewer={viewer}
          />
        ) : null),
        [flagging, hideFlagDialog, isAuthor, review, viewer],
      )} */}
    </Card>
  );
};

ReviewCard.propTypes = {
  review: PropTypes.object.isRequired,
  viewer: PropTypes.object.isRequired,
  onLikeOrUnlike: PropTypes.object.isRequired,
  onCommentCount: PropTypes.object.isRequired,
};

export default ReviewCard;
